import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Typography, CircularProgress } from "@material-ui/core";
import MessageIcon from "@material-ui/icons/Message";
import SmsIcon from "@material-ui/icons/Sms";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PersonIcon from "@material-ui/icons/Person";
import { sendOTP, verifyOTP, sendEmailOTP, verifyEmailOTP, signupWithPhone, completePhoneProfile, phoneDirectLogin } from "../../redux/apiCalls";
import { toast } from "react-toastify";
import { PHONE_COUNTRY, PHONE_PREFERRED } from "../../config/market";
import {
  OTP_CHANNEL,
  REQUESTABLE_CHANNELS,
  normalizeChannel,
  channelPhrase,
  channelTitle,
  sentCopy,
  fallbackNotice,
  channelUnavailableTitle,
  alternateChannel,
  isDeliverableChannel,
} from "./otpChannels";
import styles from "./PhoneAuth.module.css";
import { trackLogin } from "../../utils/analytics";
import { recordSignInOutcome } from "../../utils/signInOutcome";

const CHANNEL_ICONS = {
  [OTP_CHANNEL.SMS]: SmsIcon,
  [OTP_CHANNEL.WHATSAPP]: WhatsAppIcon,
  [OTP_CHANNEL.EMAIL]: EmailIcon,
};

const MAX_RESENDS = 3;

/**
 * How long the code lives, in whole minutes, from whichever field the server
 * chose to say it in. Returns null when it said nothing — we render nothing
 * rather than inventing an expiry the server never promised.
 */
const readExpiryMinutes = (result) => {
  if (Number.isFinite(result?.expiresInMinutes)) return result.expiresInMinutes;
  if (Number.isFinite(result?.otpExpiresIn)) return Math.round(result.otpExpiresIn / 60);
  return null;
};

/**
 * PhoneAuth - Unified phone authentication component
 *
 * Handles both login AND signup seamlessly:
 * 1. User enters phone + picks a channel → OTP sent (temp user created on backend if new)
 * 2. User enters OTP → verified
 * 3. If existing user → logged in immediately
 * 4. If new user (no name) → shown profile completion form
 * 5. User enters name → profile completed → logged in
 *
 * Props:
 * - onSuccess: callback after successful auth
 * - isSignup: if true, uses the phoneSignup endpoint (Register page flow)
 * - initialPhone: pre-fill phone number
 * - userName: pre-filled name (for Register page flow)
 * - initialChannel: which delivery channel is pre-selected ("sms" | "whatsapp").
 *   Defaults to WhatsApp — richer delivery, no per-message SMS cost, and it is
 *   the channel that keeps working when SMS credit runs out.
 * - onUseEmailInstead: escape hatch to the email + password form
 */
const PhoneAuth = ({
  onSuccess,
  isSignup = false,
  initialPhone = "",
  userName = "",
  initialChannel = OTP_CHANNEL.WHATSAPP,
  onUseEmailInstead,
}) => {
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [success, setSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [resendCount, setResendCount] = useState(0);

  // ── Service-level error state (e.g. Twilio auth failure) ──
  // Shown as a persistent inline banner so the user isn't stuck.
  // Shape: { message, channel } — the channel is what makes the banner able to
  // name the thing that actually broke and offer the other one.
  const [serviceError, setServiceError] = useState(null);

  // ── Delivery channel ──────────────────────────────────────────────────────
  // The customer PICKS a channel (SMS or WhatsApp — both reach a phone number,
  // so both are offered up front with equal weight; email needs an address we
  // have to ask for first).
  //
  // What they pick is a REQUEST, not a promise. The server decides what
  // actually goes out and reports it back, and it deliberately falls back:
  //   • WhatsApp not deliverable to that number → SMS;
  //   • SMS out of credit and we hold an address → email, silently, still 200;
  //   • SMS down with no address on file → we ask for one here.
  // Only the last of those changes which verify endpoint the typed code goes
  // to, which is what `emailFlow` tracks.
  //
  // `delivery` is therefore built from the RESPONSE, never from the request:
  //   { requested, actual, destination, notice, expiresInMinutes }
  // Without that distinction the screen keeps insisting the code went to a
  // phone, and a working failover still reads as a broken product.
  const [requestedChannel, setRequestedChannel] = useState(
    normalizeChannel(initialChannel) || OTP_CHANNEL.WHATSAPP
  );
  const [delivery, setDelivery] = useState(null);
  const [emailPrompt, setEmailPrompt] = useState(false);
  const [emailForOtp, setEmailForOtp] = useState("");
  const [emailFlow, setEmailFlow] = useState(false);
  /* The login / sign_up growth-event method for the door this code came in by. */
  const signInMethod = () =>
    emailFlow ? "email" : requestedChannel === OTP_CHANNEL.WHATSAPP ? "whatsapp" : "phone";

  // ── New user profile completion state ──
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [tempAccessToken, setTempAccessToken] = useState(null);

  const otpInputs = useRef([]);
  const nameInputRef = useRef(null);
  const countdownTimer = useRef(null);
  const redirectTimer = useRef(null);
  const isMounted = useRef(true);
  // Refs, not the `loading` state: React batches state updates, so two clicks
  // (or a click racing the auto-submit effect) can both observe loading=false
  // and both fire. A ref flips synchronously.
  const sendInFlight = useRef(false);
  const verifyInFlight = useRef(false);
  // Once the customer picks a channel themselves, the host page stops being
  // allowed to move it under them.
  const channelTouched = useRef(false);

  const startCountdown = () => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
    }

    setCountdown(60);
    countdownTimer.current = setInterval(() => {
      if (!isMounted.current) {
        clearInterval(countdownTimer.current);
        countdownTimer.current = null;
        return;
      }

      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer.current);
          countdownTimer.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async (e, options = {}) => {
    e?.preventDefault();

    const { isResend = false, channel } = options;
    const targetChannel = normalizeChannel(channel) || requestedChannel;

    if (sendInFlight.current) return;

    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    if (isResend && countdown > 0) {
      toast.warning(`Please wait ${countdown} seconds before resending`);
      return;
    }

    if (isResend && resendCount >= MAX_RESENDS) {
      toast.error("Maximum resend attempts reached. Please try again later or contact support.");
      return;
    }

    sendInFlight.current = true;
    setLoading(true);
    setServiceError(null); // Clear any previous service error
    try {
      const formattedPhone = `+${phoneNumber}`;
      const result = await sendOTP(formattedPhone, targetChannel);

      if (!isMounted.current) return;

      // ── Direct-login gate: verified user, skip OTP entirely ───────────────
      if (result?.directLoginAvailable) {
        const signedIn = await phoneDirectLogin(dispatch, formattedPhone);
        if (!isMounted.current) return;
        // A returning customer who never gave a name (closed the tab on the
        // profile step, or signed up in the app) is asked here, once more,
        // and can skip. The server says whether the name is still missing.
        if (signedIn?.profile?.needsName) {
          setOtpSent(true);
          setNeedsProfileCompletion(true);
          setTempAccessToken(signedIn.accessToken);
          toast.info("Welcome back! What should we call you?");
          return;
        }
        setSuccess(true);
        trackLogin("phone");
        toast.success("Welcome back!");
        handleSuccessRedirect();
        return;
      }
      // ────────────────────────────────────────────────────────────────────

      setOtpSent(true);
      // This code came in through the PHONE door, so the typed code verifies
      // against /user/verify-otp — even if the server delivered it by email.
      setEmailFlow(false);

      // Everything the screen says about delivery comes from the RESPONSE.
      // `maskedDestination` is the current contract; `sentTo` is the older
      // field name and is still honoured so a mid-deploy client keeps working.
      const actualChannel = normalizeChannel(result?.channel) || targetChannel;
      const destination =
        result?.maskedDestination || result?.sentTo || formattedPhone;
      // The server's own sentence wins when it took the trouble to explain the
      // reroute; our generated one covers the case where it just reports a
      // different `channel` and leaves the wording to us.
      const serverNotice = result?.reroutedFromSms ? result?.message : null;

      setDelivery({
        requested: targetChannel,
        actual: actualChannel,
        destination,
        notice: serverNotice || fallbackNotice(targetChannel, actualChannel),
        expiresInMinutes: readExpiryMinutes(result),
      });
      setRequestedChannel(targetChannel);

      if (isResend) {
        setResendCount((prev) => prev + 1);
        toast.success(
          `Code resent ${channelPhrase(actualChannel)} (${resendCount + 1}/${MAX_RESENDS})`
        );
      } else {
        toast.success(`Code sent ${channelPhrase(actualChannel)}`);
      }

      startCountdown();
    } catch (error) {
      if (!isMounted.current) return;

      const msg = error.message || "Failed to send OTP";
      const code = error.code;
      const failedChannel = normalizeChannel(error.channel) || targetChannel;
      const suggestEmail =
        error.suggestEmail === true ||
        code === "SMS_UNAVAILABLE" ||
        error.httpStatus === 503;
      const isServiceError =
        suggestEmail ||
        code === "CHANNEL_UNAVAILABLE" ||
        code === "WHATSAPP_UNAVAILABLE" ||
        msg.toLowerCase().includes("authentication") ||
        msg.toLowerCase().includes("service") ||
        msg.toLowerCase().includes("unavailable");

      // `emailOtpAvailable` means the server can send this code to an inbox
      // instead. Prefer that over the old banner, whose "Use email instead"
      // button drops the customer on the email+PASSWORD form — a dead end for
      // anyone who signed up by phone, because they have never set a password.
      if (error.emailOtpAvailable) {
        setEmailPrompt(true);
        setServiceError(null);
        toast.info(
          `${channelTitle(failedChannel)} isn't working right now — enter your email and we'll send your code there.`
        );
      } else if (isServiceError) {
        setServiceError({ message: msg, channel: failedChannel });
      } else {
        toast.error(msg);
      }
    } finally {
      sendInFlight.current = false;
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Passwordless email OTP — the same six digits, delivered to an inbox.
  const handleSendEmailOTP = async (e, options = {}) => {
    e?.preventDefault();

    const { isResend = false } = options;
    const email = emailForOtp.trim();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    if (sendInFlight.current) return;

    if (isResend && countdown > 0) {
      toast.warning(`Please wait ${countdown} seconds before resending`);
      return;
    }

    if (isResend && resendCount >= MAX_RESENDS) {
      toast.error("Maximum resend attempts reached. Please try again later or contact support.");
      return;
    }

    sendInFlight.current = true;
    setLoading(true);
    try {
      const result = await sendEmailOTP(email);
      if (!isMounted.current) return;

      setEmailFlow(true);
      setEmailPrompt(false);
      setServiceError(null);
      setOtpSent(true);

      const actualChannel = normalizeChannel(result?.channel) || OTP_CHANNEL.EMAIL;
      setDelivery({
        requested: OTP_CHANNEL.EMAIL,
        actual: actualChannel,
        destination: result?.maskedDestination || result?.sentTo || email,
        // They ASKED for email, so when email is what happened there is nothing
        // to apologise for — fallbackNotice returns null for a match.
        notice: fallbackNotice(OTP_CHANNEL.EMAIL, actualChannel),
        expiresInMinutes: readExpiryMinutes(result),
      });

      if (isResend) {
        setResendCount((prev) => prev + 1);
        toast.success(
          `Code resent ${channelPhrase(actualChannel)} (${resendCount + 1}/${MAX_RESENDS})`
        );
      } else {
        toast.success("Code sent — check your email.");
      }
      startCountdown();
    } catch (error) {
      if (!isMounted.current) return;
      if (error.code === "USE_PASSWORD_SIGNIN" && typeof onUseEmailInstead === "function") {
        // This account genuinely has a password (staff, or an email signup), so
        // the password form is the right destination rather than a dead end.
        toast.info(error.message);
        onUseEmailInstead();
        return;
      }
      toast.error(error.message || "Couldn't send that code");
    } finally {
      sendInFlight.current = false;
      if (isMounted.current) setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    const otpString = otp.join("");
    if (!otpString || otpString.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    if (verifyInFlight.current) return;
    verifyInFlight.current = true;
    setLoading(true);
    try {
      const formattedPhone = `+${phoneNumber}`;

      if (isSignup && userName && !emailFlow) {
        // ── Register page flow: signup with name + phone + OTP ──
        const userData = {
          name: userName,
          phoneNumber: formattedPhone,
          otp: otpString,
        };
        await signupWithPhone(dispatch, userData);
        recordSignInOutcome({ method: signInMethod(), isNewUser: true });

        if (!isMounted.current) return;

        setSuccess(true);
        toast.success("Registration successful!");
        handleSuccessRedirect();
      } else {
        // ── Login page flow: verify OTP (handles both new & existing users) ──
        // Both endpoints return the same shape, so everything below is shared —
        // only the door differs.
        const result = emailFlow
          ? await verifyEmailOTP(dispatch, emailForOtp.trim(), otpString)
          : await verifyOTP(dispatch, formattedPhone, otpString);

        recordSignInOutcome({ method: signInMethod(), isNewUser: result.isNewUser });

        if (!isMounted.current) return;

        if (result.isNewUser || result.profile?.needsName) {
          // New user, or a returning one still without a name: ask once, skippably
          setNeedsProfileCompletion(true);
          setTempAccessToken(result.accessToken);
          setLoading(false);
          toast.info("Almost there! Please enter your name to complete signup.");

          // Clear countdown timer since OTP phase is done
          if (countdownTimer.current) {
            clearInterval(countdownTimer.current);
            countdownTimer.current = null;
          }
          return; // Don't redirect yet
        }

        // Existing user — logged in successfully
        setSuccess(true);
        toast.success("Login successful!");
        handleSuccessRedirect();
      }
    } catch (error) {
      if (!isMounted.current) return;

      toast.error(error.message || "Invalid OTP");
      setOtp(["", "", "", "", "", ""]);
      if (otpInputs.current[0]) otpInputs.current[0].focus();
    } finally {
      verifyInFlight.current = false;
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // ── Skip the name: a phone-only customer must always be able to transact ──
  // The OTP verify and direct sign-in have already signed the customer in
  // (loginSuccess was dispatched with their token), so skipping only moves on.
  // Profile can take the name later, and the server adopts it from the first
  // order that carries one.
  const handleSkipProfile = () => {
    setNeedsProfileCompletion(false);
    setSuccess(true);
    toast.info("You can add your name under Profile any time.");
    handleSuccessRedirect();
  };

  // ── Handle profile completion for new users ──
  const handleCompleteProfile = async (e) => {
    e.preventDefault();

    if (!profileName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    setLoading(true);
    try {
      await completePhoneProfile(dispatch, {
        name: profileName.trim(),
        accessToken: tempAccessToken,
      });

      if (!isMounted.current) return;

      setSuccess(true);
      setNeedsProfileCompletion(false);
      toast.success("Welcome to Lusuku!");
      handleSuccessRedirect();
    } catch (error) {
      if (!isMounted.current) return;
      toast.error(error.message || "Failed to complete profile");
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleSuccessRedirect = () => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }

    setRedirecting(true);

    redirectTimer.current = setTimeout(() => {
      if (onSuccess) onSuccess();
    }, 1500);
  };

  // A resend is also the customer's chance to CHANGE channel — "it never
  // arrived" and "try the other one" are the same thought, so they are the
  // same control.
  const handleResendOTP = async (channel) => {
    if (countdown > 0) {
      toast.warning(`Please wait ${countdown} seconds before resending`);
      return;
    }

    if (resendCount >= MAX_RESENDS) {
      toast.error("Maximum resend attempts reached. Please refresh the page to try again.");
      return;
    }

    setOtp(["", "", "", "", "", ""]);

    if (otpInputs.current[0]) {
      otpInputs.current[0].focus();
    }

    if (channel === OTP_CHANNEL.EMAIL) {
      await handleSendEmailOTP(null, { isResend: true });
      return;
    }

    await handleSendOTP(null, { isResend: true, channel });
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const pastedData = value.slice(0, 6).split("");
      const newOtp = [...otp];
      pastedData.forEach((char, i) => {
        if (index + i < 6) {
          newOtp[index + i] = char;
        }
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + pastedData.length, 5);
      if (otpInputs.current[nextIndex]) {
        otpInputs.current[nextIndex].focus();
      }
    } else {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        otpInputs.current[index + 1].focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputs.current[index - 1].focus();
    }
  };

  useEffect(() => {
    if (otpSent && !needsProfileCompletion && otpInputs.current[0]) {
      otpInputs.current[0].focus();
    }
  }, [otpSent, needsProfileCompletion]);

  // The host page learns whether SMS is switched on server-side AFTER this
  // component has mounted (GET /user/auth-options is async), so `initialChannel`
  // can change under us. Honour it right up until the customer expresses a
  // preference or a code is already in flight — after that it is theirs.
  useEffect(() => {
    const next = normalizeChannel(initialChannel);
    if (!next || channelTouched.current || otpSent) return;
    setRequestedChannel(next);
  }, [initialChannel, otpSent]);

  // Focus name input when profile completion step appears
  useEffect(() => {
    if (needsProfileCompletion && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [needsProfileCompletion]);

  // Auto-submit when OTP is complete
  useEffect(() => {
    const otpString = otp.join("");
    if (otpString.length === 6 && !loading && otpSent && !success && !needsProfileCompletion) {
      handleVerifyOTP({ preventDefault: () => { } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (countdownTimer.current) {
        clearInterval(countdownTimer.current);
      }
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
      }
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  const HeroIcon = CHANNEL_ICONS[requestedChannel] || MessageIcon;
  const sentLine = sentCopy(delivery?.actual, delivery?.destination || `+${phoneNumber}`);
  const expiresIn = delivery?.expiresInMinutes;
  // Email joins the resend row only once we hold an address to send to.
  const resendChannels = [
    ...REQUESTABLE_CHANNELS.map((choice) => choice.value),
    ...(emailForOtp.trim() ? [OTP_CHANNEL.EMAIL] : []),
  ];

  return (
    <div className={styles.phoneAuthContainer}>
      {/* ── Step 1: Phone Number Input ── */}
      {!otpSent && !needsProfileCompletion ? (
        <form onSubmit={handleSendOTP} className={styles.form} aria-busy={loading}>
          <div className={styles.iconCircle}>
            <HeroIcon className={styles.mainIcon} />
          </div>
          <Typography className={styles.instructionText}>
            {/* Both kept to ONE line at this card width. The two-line versions
                cost 21px for words the phone field and the button already say. */}
            {isSignup
              ? "We'll send a code to verify this number"
              : "We'll send a code to this number"}
          </Typography>

          {/* ── SMS is down: ask for an email and stay in the OTP flow ──
              This replaces the old dead end. The banner below still exists for
              errors we can't route around, but its "Use email instead" button
              lands on the email+PASSWORD form — which a phone-registered
              customer can never complete, because they have no password.

              Deliberately NOT a <form>: this block renders inside the phone
              form, and a nested <form> is invalid HTML whose submit behaviour
              is browser-dependent. */}
          {emailPrompt && (
            <div className={styles.serviceErrorBanner}>
              <div className={styles.serviceErrorContent}>
                <span className={styles.serviceErrorIcon} aria-hidden="true">✉️</span>
                <div>
                  <Typography className={styles.serviceErrorTitle}>
                    Send my code by email instead
                  </Typography>
                  <Typography className={styles.serviceErrorMessage}>
                    We'll send your 6-digit code to your inbox. No password
                    needed.
                  </Typography>
                </div>
              </div>
              <div className={styles.nameInputContainer} style={{ marginBottom: "12px" }}>
                <EmailIcon style={{ color: "#95a5a6", marginRight: "12px", fontSize: "20px" }} />
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  aria-label="Email address for your verification code"
                  className={styles.nameInput}
                  placeholder="you@example.com"
                  value={emailForOtp}
                  onChange={(ev) => setEmailForOtp(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") {
                      ev.preventDefault();
                      handleSendEmailOTP();
                    }
                  }}
                  disabled={loading}
                />
              </div>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => handleSendEmailOTP()}
                disabled={loading || !emailForOtp.trim()}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Send code to email"
                )}
              </button>
            </div>
          )}

          {/* ── Service-level error banner ── */}
          {serviceError && (
            <div className={styles.serviceErrorBanner} role="alert">
              <div className={styles.serviceErrorContent}>
                <span className={styles.serviceErrorIcon} aria-hidden="true">⚠️</span>
                <div>
                  <Typography className={styles.serviceErrorTitle}>
                    {channelUnavailableTitle(serviceError.channel)}
                  </Typography>
                  <Typography className={styles.serviceErrorMessage}>
                    {serviceError.message}
                  </Typography>
                </div>
              </div>
              <div className={styles.serviceErrorActions}>
                {isDeliverableChannel(serviceError.channel) && (
                  <button
                    type="button"
                    className={styles.emailFallbackButton}
                    onClick={() => {
                      const next = alternateChannel(serviceError.channel);
                      setServiceError(null);
                      setRequestedChannel(next);
                      handleSendOTP(null, { channel: next });
                    }}
                    disabled={loading}
                  >
                    Try {channelTitle(alternateChannel(serviceError.channel))} instead
                  </button>
                )}
                {typeof onUseEmailInstead === "function" && (
                  <button
                    type="button"
                    className={styles.emailFallbackButton}
                    onClick={() => {
                      setServiceError(null);
                      onUseEmailInstead();
                    }}
                  >
                    Use email instead
                  </button>
                )}
                <button
                  type="button"
                  className={styles.retryButton}
                  onClick={(e) => {
                    setServiceError(null);
                    handleSendOTP(e);
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={14} color="inherit" /> : "Try Again"}
                </button>
              </div>
            </div>
          )}

          <div className={styles.inputWrapper}>
            <PhoneInput
              country={PHONE_COUNTRY}
              value={phoneNumber}
              onChange={setPhoneNumber}
              inputClass={styles.phoneInput}
              containerClass={styles.phoneInputContainer}
              buttonClass={styles.phoneInputButton}
              placeholder="Enter phone number"
              enableSearch={true}
              disableSearchIcon={false}
              countryCodeEditable={false}
              preferredCountries={PHONE_PREFERRED}
              inputProps={{ "aria-label": "Phone number" }}
            />
          </div>

          {/* ── Channel choice ──────────────────────────────────────────────
              Two equal options, not a link buried under SMS. WhatsApp is
              routinely the cheaper and more dependable path to a Ugandan
              handset, so it gets the same size, the same weight and the same
              tap target as SMS. Real radio inputs, so arrow keys and screen
              readers work without any of it being reimplemented. */}
          <fieldset className={styles.channelFieldset} disabled={loading}>
            <legend className={styles.channelLegend}>How should we send your code?</legend>
            <div className={styles.channelOptions}>
              {REQUESTABLE_CHANNELS.map(({ value, label, hint }) => {
                const ChannelIcon = CHANNEL_ICONS[value] || MessageIcon;
                const isSelected = requestedChannel === value;
                return (
                  <label
                    key={value}
                    className={`${styles.channelOption} ${isSelected ? styles.channelOptionSelected : ""}`}
                  >
                    <input
                      type="radio"
                      name="otpChannel"
                      value={value}
                      checked={isSelected}
                      onChange={() => {
                        channelTouched.current = true;
                        setRequestedChannel(value);
                      }}
                      className={styles.channelRadio}
                    />
                    <ChannelIcon className={styles.channelIcon} />
                    <span className={styles.channelLabel}>{label}</span>
                    <span className={styles.channelHint}>{hint}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={loading || !phoneNumber || phoneNumber.length < 10}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" style={{ marginRight: "8px" }} />
                <span>Sending...</span>
              </>
            ) : (
              `Send code ${channelPhrase(requestedChannel)}`
            )}
          </button>
        </form>
      ) : needsProfileCompletion && !success ? (
        /* ── Step 3: Profile Completion (new users only) ── */
        <form onSubmit={handleCompleteProfile} className={styles.form} aria-busy={loading}>
          <div className={styles.iconCircle} style={{ background: "linear-gradient(135deg, #017109, #02a812)" }}>
            <PersonIcon className={styles.mainIcon} />
          </div>
          <Typography className={styles.instructionTitle}>
            Complete Your Profile
          </Typography>
          <Typography className={styles.instructionText}>
            {emailFlow ? "Email" : "Phone"} verified. Your name goes on your orders and receipts so the rider and the shop know who they are serving.
          </Typography>

          <div className={styles.inputWrapper}>
            <div className={styles.nameInputContainer}>
              <PersonIcon style={{ color: "#95a5a6", marginRight: "12px", fontSize: "20px" }} />
              <input
                ref={nameInputRef}
                type="text"
                placeholder="Enter your name"
                aria-label="Your name"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className={styles.nameInput}
                disabled={loading}
                autoFocus
              />
            </div>
          </div>

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={loading || !profileName.trim()}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                <span>Setting up...</span>
              </>
            ) : (
              "Complete & Start Shopping"
            )}
          </button>
          <button
            type="button"
            onClick={handleSkipProfile}
            className={styles.changeNumberButton}
            disabled={loading}
          >
            Skip for now
          </button>
        </form>
      ) : (
        /* ── Step 2: OTP Verification ── */
        <form onSubmit={handleVerifyOTP} className={styles.form} aria-busy={loading}>
          {success ? (
            <div className={styles.successContainer}>
              <CheckCircleIcon className={styles.successIcon} />
              <Typography className={styles.successText}>Verified!</Typography>
              {redirecting && (
                <div className={styles.redirectingContainer}>
                  <CircularProgress size={24} style={{ color: "#017109", marginTop: "16px" }} />
                  <Typography className={styles.redirectingText}>
                    {isSignup ? "Setting up your account..." : "Redirecting..."}
                  </Typography>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className={styles.iconCircle}>
                <LockIcon className={styles.mainIcon} />
              </div>
              <Typography className={styles.instructionTitle}>
                Enter Verification Code
              </Typography>

              {/* One live region for the whole delivery story, so a screen
                  reader hears the fallback and the destination together. */}
              <div className={styles.deliveryStatus} role="status" aria-live="polite">
                {delivery?.notice && (
                  <Typography className={styles.channelNotice}>
                    {delivery.notice}
                  </Typography>
                )}
                <Typography className={styles.instructionText}>
                  {sentLine.prefix} <strong>{sentLine.destination}</strong>
                  {Number.isFinite(expiresIn) && expiresIn > 0
                    ? ` — it expires in ${expiresIn} minute${expiresIn === 1 ? "" : "s"}.`
                    : "."}
                </Typography>
              </div>

              <div className={styles.otpInputsContainer}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    aria-label={`Verification code digit ${index + 1} of 6`}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className={styles.otpBox}
                    disabled={loading}
                  />
                ))}
              </div>

              <button
                type="submit"
                className={styles.primaryButton}
                disabled={loading || otp.join("").length !== 6}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                    <span>Verifying...</span>
                  </>
                ) : (
                  isSignup ? "Verify & Sign Up" : "Verify & Login"
                )}
              </button>

              <div className={styles.resendContainer}>
                {countdown > 0 ? (
                  <Typography className={styles.countdownText}>
                    Resend code in <strong>{countdown}s</strong>
                  </Typography>
                ) : resendCount >= MAX_RESENDS ? (
                  <Typography className={styles.maxAttemptsText}>
                    Maximum attempts reached. Please refresh page to try again.
                  </Typography>
                ) : (
                  <div className={styles.resendWrapper}>
                    <Typography className={styles.resendPrompt} id="resendPrompt">
                      Didn't get it? Send it again —
                    </Typography>
                    <div
                      className={styles.resendChannels}
                      role="group"
                      aria-labelledby="resendPrompt"
                    >
                      {resendChannels.map((value) => {
                        const ChannelIcon = CHANNEL_ICONS[value] || MessageIcon;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => handleResendOTP(value)}
                            className={styles.resendChannelButton}
                            aria-label={`Resend code ${channelPhrase(value)}`}
                            disabled={loading}
                          >
                            <ChannelIcon className={styles.resendChannelIcon} />
                            <span>{channelTitle(value)}</span>
                          </button>
                        );
                      })}
                    </div>
                    {resendCount > 0 && (
                      <Typography className={styles.attemptsText}>
                        ({MAX_RESENDS - resendCount} attempt{MAX_RESENDS - resendCount !== 1 ? 's' : ''} left)
                      </Typography>
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  if (countdownTimer.current) {
                    clearInterval(countdownTimer.current);
                    countdownTimer.current = null;
                  }
                  setOtpSent(false);
                  setOtp(["", "", "", "", "", ""]);
                  setPhoneNumber("");
                  setCountdown(0);
                  setResendCount(0);
                  setSuccess(false);
                  setDelivery(null);
                  setEmailFlow(false);
                  setEmailPrompt(false);
                  setServiceError(null);
                }}
                className={styles.changeNumberButton}
              >
                Change phone number
              </button>
            </>
          )}
        </form>
      )}
    </div>
  );
};

export default PhoneAuth;
